import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Search,
  Users,
  User,
  Bell,
  Plus,
  Moon,
  Sun,
  LogOut,
  Heart,
  Grid,
  Settings
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import useBackLayer from '../../hooks/useBackLayer';
import { useUser } from '../../context/UserContext';
import { useSearchSession } from '../../context/SearchSessionContext';

const getMobileActiveColor = (isDarkMode) => (isDarkMode ? '#EFF3F7' : '#131415');

const MobileNavRoot = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    bottom: max(8px, env(safe-area-inset-bottom), var(--polka-safe-bottom, 0px));
    left: 0;
    right: 0;
    z-index: 1200;
    pointer-events: none;
    padding: 0 12px;
  }
  html[data-keyboard='true'] & { display: none; }
`;

const OutsideDismiss = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: ${({ $isExpanded }) => ($isExpanded ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.22);
    border: none;
    z-index: 0;
    pointer-events: ${({ $isExpanded }) => ($isExpanded ? 'auto' : 'none')};
    backdrop-filter: blur(1px);
    -webkit-backdrop-filter: blur(1px);
    opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
    transition: opacity 300ms ease;
  }
`;

const MorphContainer = styled.nav`
  position: relative;
  margin: 0 auto;
  width: min(100%, 470px);
  pointer-events: auto;
  z-index: 1;
  background: ${({ theme, $isExpanded }) =>
    theme.isDarkMode
      ? `rgba(36, 37, 38, ${$isExpanded ? 0.88 : 0.8})`
      : `rgba(255, 255, 255, ${$isExpanded ? 0.9 : 0.82})`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ $isExpanded }) =>
    $isExpanded
      ? '0 24px 64px rgba(0, 0, 0, 0.24), 0 8px 20px rgba(0, 0, 0, 0.16)'
      : '0 8px 24px rgba(0, 0, 0, 0.16)'};
  backdrop-filter: blur(${({ $isExpanded }) => ($isExpanded ? '24px' : '16px')});
  -webkit-backdrop-filter: blur(${({ $isExpanded }) => ($isExpanded ? '24px' : '16px')});
  border-radius: ${({ $isExpanded }) => ($isExpanded ? '28px' : '30px')};
  height: ${({ $isExpanded }) => ($isExpanded ? '290px' : '70px')};
  overflow: hidden;
  transform-origin: bottom center;
  transition:
    height 340ms cubic-bezier(0.22, 1, 0.36, 1),
    border-radius 320ms ease,
    background 320ms ease,
    box-shadow 320ms ease,
    backdrop-filter 320ms ease;
`;

const ContentStage = styled.div`
  position: relative;
  height: 100%;
  padding: 8px;
`;

const BottomRow = styled.div`
  position: absolute;
  left: 8px;
  right: 84px;
  bottom: 8px;
  height: 54px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition:
    opacity 200ms ease,
    transform 260ms ease;
  opacity: ${({ $isExpanded }) => ($isExpanded ? 0 : 1)};
  transform: translateY(${({ $isExpanded }) => ($isExpanded ? '10px' : '0')});
  pointer-events: ${({ $isExpanded }) => ($isExpanded ? 'none' : 'auto')};
  z-index: 1;
`;

const ExpandedGrid = styled.div`
  position: absolute;
  left: 8px;
  right: 84px;
  top: 8px;
  bottom: 8px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: repeat(3, minmax(0, 1fr));
  gap: 6px;
  opacity: ${({ $isExpanded }) => ($isExpanded ? 1 : 0)};
  transform: scale(${({ $isExpanded }) => ($isExpanded ? 1 : 0.92)});
  transform-origin: right bottom;
  transition:
    opacity 240ms ease 60ms,
    transform 300ms cubic-bezier(0.22, 1, 0.36, 1) 40ms;
  pointer-events: ${({ $isExpanded }) => ($isExpanded ? 'auto' : 'none')};
  z-index: 2;
`;

const RowNavItem = styled(NavLink)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 4px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 10px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 14px;
  transition: color 0.2s ease, background 0.2s ease;

  &:hover,
  &.active {
    color: ${({ theme }) => getMobileActiveColor(theme.isDarkMode)};
    background: transparent;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const GridNavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 4px;
  padding: 8px 6px;
  color: ${({ theme, $isActive }) => 
    $isActive ? getMobileActiveColor(theme.isDarkMode) : theme.colors.textSecondary};
  font-size: 11px;
  font-weight: 500;
  text-decoration: none;
  background: transparent;
  border-radius: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: transparent;
    color: ${({ theme }) => getMobileActiveColor(theme.isDarkMode)};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FandomGridIcon = styled.img`
  width: 20px;
  height: 20px;
  filter: ${({ theme }) => theme.isDarkMode
    ? 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)'
    : 'brightness(0) saturate(100%) invert(9%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)'};
  transition: filter 0.3s ease;
`;

const ShelfGridIcon = styled.img`
  width: 20px;
  height: 20px;
  filter: ${({ theme }) => theme.isDarkMode
    ? 'brightness(0) saturate(100%) invert(100%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%)'
    : 'brightness(0) saturate(100%) invert(9%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(95%) contrast(95%)'};
  transition: filter 0.3s ease;
`;

const GridActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 4px;
  padding: 8px 6px;
  color: ${({ theme, $isDanger }) =>
    $isDanger ? theme.colors.error : theme.colors.textSecondary};
  font-size: 11px;
  font-weight: 500;
  background: transparent;
  border: none;
  border-radius: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme, $isDanger }) =>
      $isDanger ? `${theme.colors.error}16` : theme.colors.surfaceHover};
    color: ${({ theme, $isDanger }) =>
      $isDanger ? theme.colors.error : theme.colors.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FabButton = styled.button`
  position: absolute;
  right: 10px;
  bottom: ${({ $isExpanded }) => ($isExpanded ? '10px' : '8px')};
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => getMobileActiveColor(theme.isDarkMode)};
  background: transparent;
  box-shadow: none;
  transform: translateY(0);
  transition:
    bottom 320ms cubic-bezier(0.22, 1, 0.36, 1),
    background 220ms ease,
    transform 220ms ease;

  &:active {
    transform: scale(0.96);
  }
`;

const FabIconWrap = styled.span`
  display: inline-flex;
  transform: rotate(${({ $isExpanded }) => ($isExpanded ? '45deg' : '0deg')});
  transition: transform 300ms cubic-bezier(0.22, 1, 0.36, 1);

  svg {
    width: 24px;
    height: 24px;
  }
`;

const BottomNavigation = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { logout } = useUser();
  const { snapshot } = useSearchSession();
  const searchPath = snapshot?.query ? '/search?' + new URLSearchParams({ q: snapshot.query, ...(snapshot.category !== 'all' ? { type: snapshot.category } : {}) }) : '/search';
  useBackLayer(() => setIsExpanded(false), isExpanded);

  const isWishlistRoute = location.pathname.startsWith('/wishlist');
  const handleNavigate = () => setIsExpanded(false);

  return (
    <MobileNavRoot>
      <OutsideDismiss $isExpanded={isExpanded} onClick={() => setIsExpanded(false)} aria-label={t('common.close')} />
      <MorphContainer $isExpanded={isExpanded}>
        <ContentStage>
          <BottomRow $isExpanded={isExpanded}>
            <RowNavItem to="/" onClick={handleNavigate}>
              <Home />
              <span>{t('nav.feed')}</span>
            </RowNavItem>

            <RowNavItem to={searchPath} onClick={handleNavigate}>
              <Search />
              <span>{t('nav.search')}</span>
            </RowNavItem>

            <RowNavItem to="/shelf" onClick={handleNavigate}>
              <ShelfGridIcon src="/polkaicon.svg" alt={t('nav.shelf')} />
              <span>{t('nav.shelf')}</span>
            </RowNavItem>
          </BottomRow>

          <ExpandedGrid $isExpanded={isExpanded}>
            <GridNavItem to="/" $isActive={location.pathname === '/'} onClick={handleNavigate}>
              <Home />
              <span>{t('nav.feed')}</span>
            </GridNavItem>
            <GridNavItem to={searchPath} $isActive={location.pathname.startsWith('/search')} onClick={handleNavigate}>
              <Search />
              <span>{t('nav.search')}</span>
            </GridNavItem>
            <GridNavItem to="/shelf" $isActive={location.pathname.startsWith('/shelf')} onClick={handleNavigate}>
              <ShelfGridIcon src="/polkaicon.svg" alt={t('nav.shelf')} />
              <span>{t('nav.shelf')}</span>
            </GridNavItem>

            <GridNavItem to="/wishlist" $isActive={isWishlistRoute} onClick={handleNavigate}>
              <Heart />
              <span>{t('nav.wishlist')}</span>
            </GridNavItem>
            <GridNavItem to="/fandoms" $isActive={location.pathname.startsWith('/fandoms')} onClick={handleNavigate}>
              <FandomGridIcon src="/fandom.svg" alt={t('nav.fandoms')} />
              <span>{t('nav.fandoms')}</span>
            </GridNavItem>
            <GridNavItem to="/collections" $isActive={location.pathname.startsWith('/collections')} onClick={handleNavigate}>
              <Grid />
              <span>{t('nav.collections')}</span>
            </GridNavItem>
            <GridNavItem to="/friends" $isActive={location.pathname.startsWith('/friends')} onClick={handleNavigate}>
              <Users />
              <span>{t('nav.friends')}</span>
            </GridNavItem>
            <GridNavItem to="/profile" $isActive={location.pathname.startsWith('/profile')} onClick={handleNavigate}>
              <User />
              <span>{t('nav.profile')}</span>
            </GridNavItem>

            <GridNavItem to="/notifications" $isActive={location.pathname.startsWith('/notifications')} onClick={handleNavigate}>
              <Bell />
              <span>{t('nav.notifications')}</span>
            </GridNavItem>
            <GridActionButton onClick={toggleTheme}>
              {isDarkMode ? <Sun /> : <Moon />}
              <span>{isDarkMode ? t('nav.lightTheme') : t('nav.darkTheme')}</span>
            </GridActionButton>
            <GridNavItem to="/settings" onClick={handleNavigate}>
              <Settings />
              <span>{t('nav.settings')}</span>
            </GridNavItem>
            <GridActionButton
              $isDanger
              onClick={() => {
                logout();
                setIsExpanded(false);
              }}
            >
              <LogOut />
              <span>{t('nav.logout')}</span>
            </GridActionButton>
          </ExpandedGrid>

          <FabButton
            type="button"
            $isExpanded={isExpanded}
            onClick={() => setIsExpanded(prev => !prev)}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? t('common.close') : t('nav.menu')}
          >
            <FabIconWrap $isExpanded={isExpanded}>
              <Plus />
            </FabIconWrap>
          </FabButton>
        </ContentStage>
      </MorphContainer>
    </MobileNavRoot>
  );
};

export default BottomNavigation;
